import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { showToast } from '../../utils/toast';
import './CirclePostDetail.css';

const LEVEL_LABELS = {
  easy: 'ゆるハイク',
  beginner: '初級',
  intermediate: '中級',
  advanced: '健脚・上級',
  snow: '雪山',
};

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
}

function CirclePostDetail() {
  const { id: circleId, postId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [circle, setCircle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    Promise.all([
      getDoc(doc(db, 'recruitments', circleId, 'posts', postId)),
      getDoc(doc(db, 'recruitments', circleId)),
    ])
      .then(([postSnap, circleSnap]) => {
        if (!active) return;
        if (!postSnap.exists()) {
          setError('投稿が見つかりませんでした。');
          return;
        }
        const postData = { id: postSnap.id, ...postSnap.data() };
        if (postData.status === 'draft' && postData.authorId !== currentUser?.uid) {
          setError('この投稿は下書きです。');
          return;
        }
        setPost(postData);
        if (circleSnap.exists()) setCircle({ id: circleSnap.id, ...circleSnap.data() });
      })
      .catch(() => { if (active) setError('データの取得に失敗しました。'); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [circleId, postId, currentUser]);

  const isOwnerOrAuthor = currentUser && post && (
    post.authorId === currentUser.uid || circle?.userId === currentUser.uid
  );

  const handleDelete = async () => {
    if (!window.confirm('この活動記録を削除しますか？この操作は元に戻せません。')) return;
    setDeleting(true);
    try {
      await updateDoc(doc(db, 'recruitments', circleId, 'posts', postId), {
        status: 'deleted',
        deletedAt: serverTimestamp(),
      });
      showToast({ message: '活動記録を削除しました。', type: 'success' });
      navigate(`/circles/${circleId}/manage`);
    } catch (err) {
      console.error('Post delete failed:', err);
      showToast({ message: '削除に失敗しました。', type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="loading">読み込み中...</div>;

  if (error) {
    return (
      <div className="cpd-page">
        <div className="cpd-error">
          <p>{error}</p>
          <Link to={`/circles/${circleId}/manage`} className="cpd-btn cpd-btn--ghost">管理画面へ戻る</Link>
        </div>
      </div>
    );
  }

  const photos = Array.isArray(post.photoUrls) ? post.photoUrls.filter(Boolean) : [];

  return (
    <div className="cpd-page">
      {post.status === 'draft' && (
        <div className="cpd-draft-banner">下書き — 公開されていません</div>
      )}

      <article className="cpd-article">
        <header className="cpd-header">
          {circle && (
            <Link to={`/circles/${circleId}/manage`} className="cpd-circle-link">
              {circle.groupName}
            </Link>
          )}
          <h1>{post.title || post.mountain}</h1>
          <div className="cpd-meta">
            <span>{formatDate(post.date)}</span>
            {post.mountain && post.title && <span>{post.mountain}</span>}
            {post.level && <span>{LEVEL_LABELS[post.level] || post.level}</span>}
            {post.weather && <span>{post.weather}</span>}
            {post.participants && <span>{post.participants}人参加</span>}
          </div>
          <div className="cpd-author">
            {post.authorPhotoURL && (
              <img src={post.authorPhotoURL} alt={post.authorNickname} className="cpd-author__avatar" />
            )}
            <span>{post.authorNickname || '登山ユーザー'}</span>
          </div>
        </header>

        {photos.length > 0 && (
          <div className="cpd-photos">
            <div className={`cpd-photo-grid cpd-photo-grid--${Math.min(photos.length, 3)}`}>
              {photos.slice(0, 6).map((url, i) => (
                <button
                  key={url}
                  type="button"
                  className="cpd-photo-btn"
                  onClick={() => setLightboxIndex(i)}
                  aria-label={`写真${i + 1}を拡大`}
                >
                  <img src={url} alt={`山行写真${i + 1}`} />
                  {i === 5 && photos.length > 6 && (
                    <span className="cpd-photo-more">+{photos.length - 6}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="cpd-body">
          {post.body.split('\n').map((line, i) => (
            line ? <p key={i}>{line}</p> : <br key={i} />
          ))}
        </div>

        {(post.course || post.courseTime) && (
          <div className="cpd-detail-block">
            <h2>コース情報</h2>
            {post.course && <p><strong>コース:</strong> {post.course}</p>}
            {post.courseTime && <p><strong>コースタイム:</strong> {post.courseTime}</p>}
          </div>
        )}

        {post.dangers && (
          <div className="cpd-detail-block cpd-detail-block--danger">
            <h2>危険箇所・注意点</h2>
            <p>{post.dangers}</p>
          </div>
        )}

        {post.gear && (
          <div className="cpd-detail-block">
            <h2>持ち物・装備</h2>
            <p>{post.gear}</p>
          </div>
        )}

        {isOwnerOrAuthor && (
          <div className="cpd-owner-actions">
            <Link to={`/circles/${circleId}/posts/${postId}/edit`} className="cpd-btn cpd-btn--ghost">
              編集する
            </Link>
            <button
              type="button"
              className="cpd-btn cpd-btn--danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? '削除中...' : '削除する'}
            </button>
          </div>
        )}
      </article>

      {lightboxIndex !== null && (
        <div
          className="cpd-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="写真を拡大表示"
          onClick={() => setLightboxIndex(null)}
        >
          <img
            src={photos[lightboxIndex]}
            alt={`写真${lightboxIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="cpd-lightbox-close"
            onClick={() => setLightboxIndex(null)}
            aria-label="閉じる"
          >
            ×
          </button>
          {lightboxIndex > 0 && (
            <button
              type="button"
              className="cpd-lightbox-prev"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => i - 1); }}
              aria-label="前の写真"
            >
              ‹
            </button>
          )}
          {lightboxIndex < photos.length - 1 && (
            <button
              type="button"
              className="cpd-lightbox-next"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => i + 1); }}
              aria-label="次の写真"
            >
              ›
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default CirclePostDetail;
